import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AppState } from '../store/state';
import { AppDispatch } from '../store/store';
import { Conference, Announcement, Role, Track } from '../store/conference/types';
import {
  selectCanViewAuthors,
  selectConference,
  selectCurrentConference,
  selectCurrentRole,
  selectCurrentTrack,
  selectCurrentUser,
  selectFormState,
  selectKeywordState,
  selectSubmissionState,
  selectTrackRoles,
} from '../store/selectors';
import Loading from '../components/Loading/Loading';
import { can, SubmissionPermissionManager } from '../helpers/permissions';
import { ConfirmAuthorshipDTO, Submission } from '../store/submission/types';
import { Button, Link } from '../components/ui';
import EditIcon from '../icons/Edit';
import SubmissionCard from '../components/SubmissionCard/SubmissionCard';
import { User } from '../store/user/types';
import useTheme from '@material-ui/core/styles/useTheme';
import styled, { ThemeProvider } from 'styled-components';
import history from '../store/history';
import { fillRoutePath } from '../helpers/path';
import { getRouteByName } from '../router/routes';
import { RowAction } from '../components/types';
import TrashCan from '../icons/TrashCan';
import DeleteSubmissionDialog from '../components/dialogs/DeleteSubmissionDialog/DeleteSubmissionDialog';
import AnnouncementSidePanel from '../components/AnnouncementSidePanel/AnnouncementSidePanel';
import { getFormsData } from '../helpers/form';
import { FormTypeEnum } from '../store/form/types.d';
import ViewNewIcon from '../icons/ViewNew';
import { useTranslation } from 'react-i18next';
import EditSubmission from '../icons/EditSubmission';
import submissionSlice from '../store/submission/slice';
import SimpleDialog from '../components/dialogs/SimpleDialog/SimpleDialog';
import Message from '../components/ui/display/Message/Message';
import Alert from '../icons/Alert';
import { getPaperStatusCategory, getPaperStatusName } from '../helpers/submission';
import { CategoryEnum } from '../store/paper-status/types.d';
import { useIsMobile } from '../helpers/window';

interface Props {
  loading: boolean;
  conference: Conference | null;
  track: Track | null;
  role: Role | null;
  submissionsById: { [key: string]: Submission };
  user: User;
  canViewAuthors: boolean;
  roleById: { [key: string]: Role };
  announcementById: { [key: string]: Announcement };
  confirmAuthorshipAction: (data: ConfirmAuthorshipDTO) => void;
}

const SubmissionsPage: React.FC<Props> = ({
  loading,
  conference,
  track,
  role,
  submissionsById,
  user,
  canViewAuthors,
  roleById,
  announcementById,
  confirmAuthorshipAction,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);
  const isMobile = useIsMobile();

  const getActions = (
    submission: Submission,
    role: Role,
    canEdit: boolean,
    isPrimaryAuthor: boolean,
    isPendingAuthor: boolean,
  ): RowAction[] => {
    const actions: RowAction[] = [];

    if (isPendingAuthor) {
      actions.push({
        id: '',
        label: '',
        callback: () => {},
        element: (
          <Message severity="warning" handleClose={() => {}} hideClose={true} className="mx-auto w-full pending-alert">
            <div className="flex justify-between mb-4">
              <Alert color="#fcd34d" />
              <p>{t('You have been added as an author for this paper, please confirm your authorship')}</p>
            </div>
            <div className="alert">
              <Button
                className="cursor-pointer pl-6 pr-6 w-fit"
                onClick={() => confirmAuthorshipAction({ submission_id: submission.id, confirm: true })}
                style={{ backgroundColor: '#fcd34d', borderColor: '#fcd34d', width: '100%' }}
              >
                <span className="text-black">{t('Confirm')}</span>
              </Button>

              <Button
                className="cursor-pointer pl-6 pr-6 w-fit"
                onClick={() =>
                  setDialog(
                    <SimpleDialog
                      title={`${t('Quit submission')} #${submission.external_id}`}
                      handleOk={() => confirmAuthorshipAction({ submission_id: submission.id, confirm: false })}
                      handleClose={() => setDialog(undefined)}
                    >
                      <p>{t('By quitting you will no longer be author of this submission.')}</p>
                      <p> {t('Are you sure you want to proceed?')}</p>
                    </SimpleDialog>,
                  )
                }
                style={{ backgroundColor: '#fcd34d', borderColor: '#fcd34d', width: '100%' }}
              >
                <span className="text-black">{t('Quit')}</span>
              </Button>
            </div>
          </Message>
        ),
      });
    } else {
      actions.push({
        id: '',
        label: '',
        callback: () => {},
        element: (
          <Link
            className="cursor-pointer text-sm flex items-center"
            onClick={() =>
              history.push(
                fillRoutePath(getRouteByName('RouteDetailSubmission').path, {
                  id: submission.external_id.toString(),
                }),
              )
            }
          >
            <ViewNewIcon />
            <span className="ml-1">{t('View')}</span>
          </Link>
        ),
      });

      if (canEdit) {
        actions.push({
          id: '',
          label: '',
          callback: () => {},
          element: (
            <Link
              className="flex items-center cursor-pointer"
              onClick={(e) =>
                history.push(
                  fillRoutePath(getRouteByName('RouteEditSubmission').path, { id: submission.external_id.toString() }),
                )
              }
            >
              <EditIcon color={theme.palette.primary.main} className="mr-1" />
              {t('Edit')}
            </Link>
          ),
        });
      }

      if (isPrimaryAuthor && getPaperStatusCategory(submission) != CategoryEnum.Rejection)
        actions.push({
          id: '',
          label: '',
          callback: () => {},
          element: (
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                if (role) {
                  setDialog(
                    <DeleteSubmissionDialog
                      open={true}
                      handleClose={() => setDialog(undefined)}
                      submission={submission}
                      role={role}
                    />,
                  );
                }
              }}
              style={{ color: theme.palette.primary.main }}
            >
              <TrashCan stroke={theme.palette.primary.main} />
              <span className="ml-1">{t('Withdraw')}</span>
            </div>
          ),
        });
    }

    return actions;
  };

  if (!conference || !track || !role || loading) {
    return <Loading />;
  }

  const announcement = announcementById[role.id];

  const authorRole = Object.values(roleById).find((role) => role.type == 'author');
  if (!authorRole) throw Error(t('Author role does not exist.'));

  /* Decide what to display */
  let content;
  if (Object.values(submissionsById).length > 0) {
    content = (
      <StyledCardsWrapper className="flex justify-start flex-wrap grow">
        {Object.values(submissionsById).map((submission, index) => {
          const spm = new SubmissionPermissionManager(submission);
          const isOwner = spm.isPrimaryAuthor(user.person.id, role);
          const submissionFormsData = getFormsData(
            FormTypeEnum.Submission,
            ['read', 'write'],
            role.id,
            isOwner ? null : authorRole.id,
            user.person.id,
            submission,
          );
          const newRegister = { ...(submissionFormsData.answerable as Submission) };

          const forms = submissionFormsData.formsData.map((value) => value.form);
          const canEdit = spm.canEdit(user, role, forms);
          const canViewPaperStatus = spm.canViewPaperStatus(user, role, forms);
          return (
            <div key={index} className="mb-6 w-80 mobileCtrlSubmissionsPageCardWrapper">
              <SubmissionCard
                submission={newRegister}
                actions={getActions(
                  submission,
                  role,
                  canEdit,
                  isOwner,
                  track.author_confirmation && spm.isPendingAuthor(user.person.id, role),
                )}
                validationStatus={
                  !canViewPaperStatus ? { title: t('Submission'), status: newRegister.validation_status } : undefined
                }
                canViewAuthors={canViewAuthors}
                authorConfirmation={track.author_confirmation}
                personId={user.person.id}
                role={role}
              />
            </div>
          );
        })}
      </StyledCardsWrapper>
    );
  } else if (can('CAN_NEW_SUBMISSION')) {
    content = (
      <div className="h-full flex justify-center items-center">
        <StyledMessageWrapper className="flex justify-center items-center flex-col mb-6 mobileCtrlSubmissionsPageMessageWrapper">
          <StyledIcon>
            <EditSubmission />
          </StyledIcon>
          <h2 className="text-xl font-bold w-max mt-3">{t('Get started with a new submission')}</h2>
          <p className="mt-3 mb-6 text-center">
            {t('Thank you for your interest, submit your paper to be part of the review process.')}
          </p>

          <div className="flex items-center mb-8">
            <StyledButton
              className="w-32 place-self-start"
              onClick={() => history.push(fillRoutePath(getRouteByName('RouteNewSubmission').path))}
            >
              <span className="px-14">{t('New submission')}</span>
            </StyledButton>
          </div>
        </StyledMessageWrapper>
      </div>
    );
  } else {
    content = (
      <div className="h-full flex justify-center items-center">
        <StyledMessageWrapper className="flex justify-center items-center flex-col mb-6">
          <StyledIcon>
            <EditSubmission />
          </StyledIcon>
          <h2 className="text-xl font-bold w-max mt-3">{t('Submission phase is closed')}</h2>
          <p className="mt-3 mb-6 text-center">{t('New submissions are not accepted at the moment.')}</p>
        </StyledMessageWrapper>
      </div>
    );
  }

  const newSubmissionButton = isMobile ? (
    <div className="flex justify-between mobileCtrlNewSubmissionButtonWrapper">
      <StyledNewSubmissionButton
        className="place-self-center"
        onClick={() => history.push(fillRoutePath(getRouteByName('RouteNewSubmission').path))}
      >
        +
      </StyledNewSubmissionButton>

      <span className="">New</span>
    </div>
  ) : (
    <Button
      className="mb-3 w-48 place-self-center"
      onClick={() => history.push(fillRoutePath(getRouteByName('RouteNewSubmission').path))}
    >
      <span className="px-14">{t('New submission')}</span>
    </Button>
  );

  return (
    <ThemeProvider theme={theme}>
      <div className="h-full flex mobileCtrlSubmissionsPageContent">
        <StyledContainer className="flex flex-col overflow-y-auto overflow-x-hidden mr-2 mobileCtrlSubmissionsPageWrapper">
          {Object.values(submissionsById).length > 0 && (
            <div className="flex items-center mb-6 mobileCtrlSubmissionsPageHeader">
              <h2 className="font-bold text-xl mb-4 mr-5">{t('Your submissions')}</h2>
              {can('CAN_NEW_SUBMISSION') && newSubmissionButton}
            </div>
          )}
          {content}
        </StyledContainer>
        {announcement && <AnnouncementSidePanel conference={conference} announcement={announcement} />}
      </div>
      {dialog}
    </ThemeProvider>
  );
};

const mapStateToProps = (state: AppState) => ({
  loading: selectSubmissionState(state).loading || selectKeywordState(state).loading || selectFormState(state).loading,
  conference: selectCurrentConference(state),
  track: selectCurrentTrack(state),
  role: selectCurrentRole(state),
  submissionsById: selectSubmissionState(state).submissionsById,
  user: selectCurrentUser(state),
  canViewAuthors: selectCanViewAuthors(state),
  roleById: selectTrackRoles(state),
  announcementById: selectConference(state).announcementById,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  confirmAuthorshipAction: (data: ConfirmAuthorshipDTO) => dispatch(submissionSlice.actions.CONFIRM_AUTHORSHIP(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SubmissionsPage);

const StyledCardsWrapper = styled.div`
  margin-left: -0.5rem;
  margin-right: -0.5rem;
`;

const StyledContainer = styled.div`
  flex-grow: 1;

  .custom-cols-4 {
    grid-template-columns: repeat(4, minmax(0, 25%));
  }

  .custom-cols-5 {
    grid-template-columns: repeat(5, minmax(0, 20%));
  }
`;

const StyledMessageWrapper = styled.div`
  max-width: 30rem;

  h2 {
    font-size: 1.45rem;
  }
  p {
    font-size: 1.1rem;
    line-height: 1.5rem;
  }
`;

const StyledIcon = styled.div`
  svg {
    width: 7.5rem;
    height: 7.5rem;
  }
`;

const StyledButton = styled(Button)`
  padding-top: 1.25rem;
  padding-bottom: 1.25rem;
  padding-left: 5.5rem;
  padding-right: 5.5rem;
  font-size: 0.975rem;
`;

const StyledNewSubmissionButton = styled(Button)`
  border-radius: 4px;
  height: auto;
  line-height: 1;
  padding: 0.2rem;

  span {
    line-height: 0.6;
    font-size: 2rem;
    font-weight: 300;
  }
`;
